<script setup>
import { computed, unref, watch, reactive, useSlots, useTemplateRef, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useScroll, useResizeObserver } from '@vueuse/core'

import BaseButton from '@dogfy/components/base/BaseButton.vue'
import AppButtonX from '@dogfy/components/layout/AppButtonX.vue'

import { useBreakpoints } from '@dogfy/composables/useBreakpoints'

const isOpen = defineModel({ type: Boolean, default: false })

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'medium',
    validator (value) {
      return ['small', 'medium', 'large', 'largest'].includes(value)
    },
  },
  primaryButtonText: {
    type: String,
    default: '',
  },
  secondaryButtonText: {
    type: String,
    default: '',
  },
  validations: {
    type: [Array, Object],
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  canContinue: {
    type: Boolean,
    default: true,
  },
  isPaddingless: {
    type: Boolean,
    default: false,
  },
  isFullheight: {
    type: Boolean,
    default: false,
  },
  isAnimationDisabled: {
    type: Boolean,
    default: false,
  },
  hasPrimaryButton: {
    type: Boolean,
    default: true,
  },
  hasSecondaryButton: {
    type: Boolean,
    default: true,
  },
  hasSmallPadding: {
    type: Boolean,
    default: false,
  },
  hasActions: {
    type: Boolean,
    default: true,
  },
  canClose: {
    type: Boolean,
    default: true,
  },
  isSelfClose: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['confirm', 'close', 'back'])
const { t } = useI18n()

const state = reactive({
  step: 1,
})

const { isMobileOrTablet } = useBreakpoints()

const contentRef = useTemplateRef('content')
const { arrivedState } = useScroll(contentRef)

const hasVerticalScrollbar = ref(false)

const slots = useSlots()

const currentValidation = unref(props.validations?.[state.step - 1] || props.validations)

useResizeObserver(contentRef, (entries) => {
  const currentEl = entries[0]?.target
  hasVerticalScrollbar.value = (currentEl?.scrollHeight || 0) > (currentEl?.clientHeight || 0)
  arrivedState.bottom = false
})

const stepsSlots = computed(() => slots.default ? ['default'] : Object.keys(slots).filter(slotName => !['default', 'footnote', 'actions'].includes(slotName)))
const totalSteps = computed(() => stepsSlots.value.length)

const isFirstStep = computed(() => state.step === 1)
const isLastStep = computed(() => state.step === totalSteps.value)

const primaryButtonLabel = computed(() => isLastStep.value ? props.primaryButtonText || t('common.cta.confirm') : t('common.cta.continue'))
const secondaryButtonLabel = computed(() => isFirstStep.value ? props.secondaryButtonText || t('common.cta.cancel') : t('common.cta.back'))

function blockPointerEvents () {
  document.body.style.pointerEvents = 'none'
}

function allowPointerEvents () {
  document.body.style.pointerEvents = 'auto'
}

function goToPrevStep () {
  if (currentValidation) currentValidation.$reset()

  if (isFirstStep.value) {
    isOpen.value = false
    emit('close')
  } else {
    state.step -= 1
    emit('back')
  }
}

async function goToNextStep () {
  if (!props.canContinue) return

  if (currentValidation) {
    const hasValidData = await currentValidation.$validate() ?? true
    if (!hasValidData) return
  }

  if (isLastStep.value) {
    emit('confirm')
  } else {
    state.step += 1
  }
}

function handleCloseEvent () {
  if (props.isSelfClose) {
    isOpen.value = false
  } else {
    emit('close')
  }
}

watch(isOpen, (isOpen) => {
  if (document) {
    document.body.style.overflow = isOpen ? 'hidden' : 'visible'
  }

  if (isOpen) {
    if (currentValidation) currentValidation.$reset()

    state.step = 1

    arrivedState.bottom = false
  }
}, { immediate: true })

defineExpose({ goToNextStep })
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="isOpen" class="overlay" role="presentation" />
    </Transition>
    <Transition :name="props.isAnimationDisabled ? null : 'move-up'" @before-enter="blockPointerEvents" @after-enter="allowPointerEvents">
      <div
        v-if="isOpen"
        class="base-modal"
        :class="{ 'base-modal--fullheight': props.isFullheight }"
        role="dialog"
        :aria-label="props.title"
        aria-modal="true"
      >
        <div
          class="base-modal__container"
          :class="[
            `base-modal__container--${props.size}`,
            { 'base-modal__container--fullheight': props.isFullheight },
          ]"
        >
          <div
            v-if="props.title || props.canClose"
            class="base-modal__header"
            :class="{
              'base-modal__header--small-padding': props.hasSmallPadding,
            }"
          >
            <p v-if="props.title">
              {{ props.title }}
            </p>
            <AppButtonX v-if="props.canClose" class="base-modal__button-close" @click="handleCloseEvent" />
          </div>
          <div
            ref="content"
            class="base-modal__content"
            :class="{
              'base-modal__content--paddingless': props.isPaddingless,
              'base-modal__content--small-padding': props.hasSmallPadding,
            }"
          >
            <slot v-if="!!$slots.default" />
            <slot v-else :key="state.step" :name="stepsSlots[state.step - 1]" />
          </div>
          <div
            v-if="props.hasActions"
            class="base-modal__actions"
            :class="{
              'base-modal__actions--small-padding': props.hasSmallPadding,
              'base-modal__actions--shadowed': hasVerticalScrollbar && !arrivedState.bottom,
            }"
          >
            <slot v-if="!!$slots.footnote" name="footnote" />
            <slot v-if="!!$slots.actions" name="actions" />
            <div v-else class="buttonset buttonset--small" :class="{ 'buttonset--column': isMobileOrTablet }">
              <BaseButton
                v-if="props.hasSecondaryButton"
                type="small fullwidth secondary"
                data-test-id="btnModalSecondary"
                @click="goToPrevStep"
              >
                {{ secondaryButtonLabel }}
              </BaseButton>
              <BaseButton
                v-if="props.hasPrimaryButton"
                type="small fullwidth"
                :is-loading="props.isLoading"
                :disabled="!props.canContinue"
                loading-overlay-on=".base-modal__container"
                data-test-id="btnModalPrimary"
                @click="goToNextStep"
              >
                {{ primaryButtonLabel }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss">
.base-modal {
  position: fixed;
  z-index: z-number(modal);

  display: flex;

  align-items: flex-end;
  justify-content: center;

  height: 100%;
  max-height: calc(100dvh - $header-nav-height);

  inset: auto 0 0;

  @include breakpoint(lg) {
    display: grid;

    grid-template-columns: repeat(12, 1fr);
    gap: $spacer*1.5;
    align-items: center;

    animation: none;

    margin: 0 auto;
    box-sizing: border-box;
    padding: 0 $spacer*3;
    max-width: $max-content-width;
    max-height: unset;

    inset: 0;
  }

  &--fullheight {
    @include breakpoint(lg) {
      align-items: flex-start;
    }
  }

  &__container {
    position: relative;

    display: flex;

    flex-direction: column;

    overflow: hidden;

    width: 100%;
    min-width: 288px;
    height: fit-content;
    min-height: 160px;
    max-height: 100%;

    background-color: $color-neutral-white;

    border-top-left-radius: $border-radius-xl;
    border-top-right-radius: $border-radius-xl;

    @include breakpoint(lg) {
      grid-column: 1 / -1;

      border-radius: $border-radius-lg;
      max-height: calc(100dvh - $header-nav-height*2);
    }

    &--small {
      @include breakpoint(lg) {
        grid-column: 5 / 9;
      }
    }

    &--medium {
      @include breakpoint(lg) {
        grid-column: 4 / 10;
      }
    }

    &--large {
      @include breakpoint(lg) {
        grid-column: 3 / 11;
      }
    }

    &--largest {
      @include breakpoint(lg) {
        // As per design requirements, the element must be 11/12 of the screen width plus the gap width.
        grid-column: 1 / -1;

        margin: 0 auto;
        width: calc(((100% / 12) * 11) + $spacer*1.5);
      }
    }

    &--fullheight {
      height: 100%;

      @include breakpoint(lg) {
        margin-top: $header-nav-height;
        max-height: calc(100dvh - $header-nav-height);

        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }

  &__header {
    display: flex;

    align-items: flex-end;
    justify-content: space-between;

    transition: box-shadow $transition-duration-fastest linear;

    box-sizing: border-box;
    padding: $spacer $spacer $spacer-half;
    min-height: 56px;

    font-size: ms(1, true);
    line-height: $font-lineheight-long;
    font-weight: $font-weight-semibold;

    @include breakpoint(lg) {
      padding: $spacer-three-quarters;
      padding-left: $spacer*3;

      font-size: ms(2, true);
      line-height: $font-lineheight-large;
    }

    &--small-padding {
      @include breakpoint(lg) {
        padding-left: $spacer*1.5;
      }
    }
  }

  &__button-close {
    margin: auto;
    margin-right: 0;
  }

  &__content {
    position: relative;

    box-sizing: border-box;
    padding: $spacer*1.5 $spacer;

    background-color: $color-neutral-white;

    overflow-y: overlay;

    @include breakpoint(lg) {
      padding: $spacer*1.5 $spacer*3 0;

      overflow-y: scroll;
      scrollbar-color: $color-neutral-semidark;
      scrollbar-width: 5px;

      &::-webkit-scrollbar {
        z-index: z-number(overbase);

        width: 5px;
      }

      &::-webkit-scrollbar-thumb {
        border-radius: $border-radius-lg;
      }

      &:hover::-webkit-scrollbar-thumb {
        background-color: $color-neutral-semidark;
      }
    }

    &--paddingless {
      padding: unset;
    }

    &--small-padding {
      @include breakpoint(lg) {
        padding: $spacer*1.5 $spacer*1.5 0;
      }
    }
  }

  &__actions {
    position: sticky;
    bottom: 0;

    display: flex;

    flex-direction: column;
    gap: $spacer;
    align-items: center;

    box-sizing: border-box;
    padding: $spacer;
    padding-bottom: $spacer-half;
    width: 100%;

    background-color: $color-neutral-white;

    @include breakpoint(lg) {
      position: relative;

      justify-content: center;

      box-shadow: none;
      padding: $spacer*1.5 $spacer*3;
    }

    &--small-padding {
      @include breakpoint(lg) {
        padding: $spacer*1.5;
      }
    }

    &--shadowed {
      box-shadow: $drop-shadow-s;
    }
  }
}
</style>
